import { Divider, Typography, Button, Popover, Tooltip, Grid, Box } from '@vapor/material'
import { Stack } from '@mui/material'
import { useTranslation, Trans } from 'react-i18next'
import { ReservationHeaderComponentsCommonProps } from './header'
import { ReferenceCustomerFragment, ReservationStateFragment, ReservedResourcePhase, TagFragment } from 'src/api/api.generated'
import useFormatDate from 'src/app/hooks/current-locale/use-format-date'
import React, { useCallback, useMemo, useState } from 'react'
import { ReservationF } from '../types/reservation-detail.types'
import { HiCIcon } from 'src/app/components/icons/icon'
import { State } from './state'
import { displayCancellationPenaltyType, displayCancellationPolicyType, displayNoShowType, displayPaymentType } from 'src/utils/booking-policy'
import { getPortalIcon } from 'src/utils/portal-utils'
import { subDays, addHours, addMinutes, getHours, getMinutes } from 'date-fns'
import { IconDefinition, IconName, library } from '@fortawesome/fontawesome-svg-core'
import { fas } from '@fortawesome/free-solid-svg-icons'
import { far } from '@fortawesome/free-regular-svg-icons'
import { FontAwesomeIcon, FontAwesomeIconProps } from '@fortawesome/react-fontawesome'
import {
  faUsers,
  faUser,
  faMoon,
  faClock,
  faPhone,
  faAt,
  faCheck,
  faHourglassStart,
  faCopy,
  faBuilding,
  faBed,
  faHandshakeAngle,
  faShieldHalved,
  faUserGroup,
  faDoorClosed,
  faPlaneArrival,
  faPlaneDeparture,
  faHashtag,
  faGlobe,
} from '@fortawesome/pro-light-svg-icons'
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons'
import logots from 'src/assets/teamsystem.gif'
import { HeaderActionMenu } from './header-action-menu'
import { getNights } from 'src/utils/date'
import { HicChip } from 'src/app/components/hic-chip/hic-chip'
import useSuccessSnackBar from 'src/app/hooks/success-snack-bar/use-success-snack-bar'
import { useProperty, useTenant } from 'src/app/hooks/context/context.hook'
import PrivacyNotConfiguredWarning from 'src/app/components/privacy-not-configured-warning/privacy-not-configured-warning'
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons'
import { sendWhatsappMessage } from 'src/utils/whatsapp-utils'
import HicChipForTag from 'src/app/components/hic-chip/hic-chip-for-tag'
import useBreakpoints from 'src/app/hooks/breakpoints/use-breakpoints'

export const Portal = React.memo(
  function Portal({ reservation }: ReservationHeaderComponentsCommonProps) {
    if (reservation.portalInfo?.portal?.name) {
      return <img src={getPortalIcon(reservation.portalInfo?.portal.name)} width={16} height={16} />
    }
    return <></>
  },
  (prev, next) => prev.reservation.portalInfo?.portal?.name === next.reservation.portalInfo?.portal?.name,
)

export const ReservationCode = React.memo(
  function ReservationCode({ reservation }: ReservationHeaderComponentsCommonProps) {
    const { t } = useTranslation()

    return (
      <Tooltip title={t('pms.booking.reservation.reservation_code')}>
        <span>
          <Stack direction="row" alignItems="center">
            <HiCIcon>
              <FontAwesomeIcon icon={faHashtag} />
            </HiCIcon>
            <Typography variant="caption" ml={0.5}>
              {reservation.reservationCode}
            </Typography>
          </Stack>
        </span>
      </Tooltip>
    )
  },
  (prev, next) => prev.reservation.reservationCode === next.reservation.reservationCode,
)

export const DatesRange = React.memo(
  function DatesRange({ reservation }: ReservationHeaderComponentsCommonProps) {
    const formatDate = useFormatDate()

    if (reservation.minStart && reservation.maxEnd) {
      return (
        <Stack direction="row" alignItems="center" gap={1}>
          <HiCIcon>
            <FontAwesomeIcon icon={faPlaneArrival} />
          </HiCIcon>
          <Typography variant="caption" mr={1}>
            {formatDate(reservation.minStart, 'iii, PPP')}
          </Typography>
          <HiCIcon>
            <FontAwesomeIcon icon={faPlaneDeparture} />
          </HiCIcon>
          <Typography variant="caption" mr={1}>
            {formatDate(reservation.maxEnd, 'iii, PPP')}
          </Typography>
          <HiCIcon>
            <FontAwesomeIcon icon={faMoon} />
          </HiCIcon>
          <Typography variant="caption">{getNights(reservation.minStart, reservation.maxEnd)}</Typography>
        </Stack>
      )
    }
    return <></>
  },
  (prev, next) => prev.reservation.minStart?.valueOf() === next.reservation.minStart?.valueOf() && prev.reservation.maxEnd?.valueOf() === next.reservation.maxEnd?.valueOf(),
)

function getGuestsCount(reservation: ReservationF): number {
  return reservation.reservedResources?.reduce<number>((previousValue, currentValue) => previousValue + currentValue.adults + currentValue.children, 0) || 0
}

export const GuestsCount = React.memo(
  function GuestsCount({ reservation }: ReservationHeaderComponentsCommonProps) {
    const { t } = useTranslation()

    return (
      <Tooltip title={t('pms.booking.reservation.guests_count')}>
        <span>
          <Stack direction="row" alignItems="center">
            <HiCIcon>
              <FontAwesomeIcon icon={faUserGroup} />
            </HiCIcon>
            <Typography variant="caption" ml={0.5} mr={1}>
              {getGuestsCount(reservation)}
            </Typography>
          </Stack>
        </span>
      </Tooltip>
    )
  },
  (prev, next) => getGuestsCount(prev.reservation) === getGuestsCount(next.reservation),
)

export const ReservedResourcesCount = React.memo(
  function ReservedResourcesCount({ reservation }: ReservationHeaderComponentsCommonProps) {
    const { t } = useTranslation()

    return (
      <Tooltip title={t('pms.booking.reservation.resources_count')}>
        <span>
          <Stack direction="row" alignItems="center">
            <HiCIcon>
              <FontAwesomeIcon icon={faDoorClosed} />
            </HiCIcon>
            <Typography variant="caption" ml={0.5} mr={1}>
              {reservation.reservedResources?.length || 0}
            </Typography>
          </Stack>
        </span>
      </Tooltip>
    )
  },
  (prev, next) => prev.reservation.reservedResources?.length === next.reservation.reservedResources?.length,
)

export function getDescription(reservation: ReservationF): string {
  if (reservation.isGroup && reservation.group) return reservation.group
  if (reservation.reference.customerPerson?.name) return reservation.reference.customerPerson.name
  if (reservation.reference.customerCompany?.name) return reservation.reference.customerCompany.name
  if (reservation.reference.agency?.name) return reservation.reference.agency.name
  if (reservation.reference.mainGuest?.name) return reservation.reference.mainGuest.name
  return ''
}

export const Description = React.memo(
  function Description({
    reservation,
    readOnly,
    onDelete,
    productionIds,
    billIds,
    documentIds,
  }: ReservationHeaderComponentsCommonProps & { onDelete: () => void; productionIds: string[]; billIds: string[]; documentIds: string[] }) {
    const { sm } = useBreakpoints()
    return (
      <>
        {sm ? (
          <Stack gap={1}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1} display={'flex'}>
              <Stack direction="row" display={'flex'} overflow={'hidden'} flexWrap={'wrap'} alignItems={'center'} flexGrow={1}>
                <Typography
                  variant="titleMedium"
                  sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flexGrow: 1, flexShrink: 0, maxWidth: '300px', pr: 1 }}
                  title={getDescription(reservation)}
                >
                  <FontAwesomeIcon icon={reservation.isGroup ? faUsers : faUser} style={{ marginRight: '0.5rem' }} />
                  {getDescription(reservation)}
                </Typography>
                <Stack direction="row" spacing={1.5} useFlexGap sx={{ alignItems: 'center', flexWrap: 'wrap', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {reservation.reference.customerPerson?.name && <CustomerPerson name={reservation.reference.customerPerson?.name} />}
                  {reservation.reference.mainGuest?.name && <MainGuest name={reservation.reference.mainGuest?.name} />}
                  {reservation.reference.customerCompany?.name && <CustomerCompany name={reservation.reference.customerCompany?.name} />}
                  {reservation.reference.agency?.name && <Agency name={reservation.reference.agency?.name} />}
                </Stack>
              </Stack>
              <Stack sx={{ backgroundColor: 'white', width: 50 }}>
                <HeaderActionMenu readOnly={readOnly} reservation={reservation} onDelete={onDelete} productionIds={productionIds} billIds={billIds} documentIds={documentIds} />
              </Stack>
            </Stack>
            <Divider light />
            <Stack direction="row" justifyContent="space-between" gap={1} alignItems="center" flexWrap="wrap">
              <Stack direction="row" gap={1} alignItems="center">
                <GuestsCount readOnly={readOnly} reservation={reservation} />
                <ReservedResourcesCount readOnly={readOnly} reservation={reservation} />
                <ReservationCode readOnly={readOnly} reservation={reservation} />
              </Stack>
              <DatesRange readOnly={readOnly} reservation={reservation} />
              <Stack direction="row" gap={1} alignItems="center">
                <CreationDate readOnly={readOnly} reservation={reservation} />
                <Origin readOnly={readOnly} reservation={reservation} />
                <CustomerLanguage readOnly={readOnly} reservation={reservation} />
              </Stack>
              <State readOnly={readOnly} reservation={reservation} />
            </Stack>
          </Stack>
        ) : (
          <Stack gap={1}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
              <Typography variant="titleMedium" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', alignSelf: 'end' }}>
                <FontAwesomeIcon icon={reservation.isGroup ? faUsers : faUser} style={{ marginRight: '0.5rem' }} />
                {getDescription(reservation)}
              </Typography>
              <HeaderActionMenu readOnly={readOnly} reservation={reservation} onDelete={onDelete} productionIds={productionIds} billIds={billIds} documentIds={documentIds} />
            </Stack>
            <Stack spacing={1} useFlexGap overflow={'hidden'}>
              <Grid container spacing={1}>
                {reservation.reference.customerPerson?.name && (
                  <Grid item xs={6}>
                    <CustomerPerson name={reservation.reference.customerPerson?.name} />
                  </Grid>
                )}
                {reservation.reference.mainGuest?.name && (
                  <Grid item xs={6}>
                    <MainGuest name={reservation.reference.mainGuest?.name} />
                  </Grid>
                )}
                {reservation.reference.customerCompany?.name && (
                  <Grid item xs={6}>
                    <CustomerCompany name={reservation.reference.customerCompany?.name} />
                  </Grid>
                )}
                {reservation.reference.agency?.name && (
                  <Grid item xs={6}>
                    <Agency name={reservation.reference.agency?.name} />
                  </Grid>
                )}
              </Grid>
            </Stack>
            <Divider light />
            <Stack direction="row" justifyContent="space-between" gap={1} alignItems="center">
              <Stack direction="row" gap={1} alignItems="center">
                <GuestsCount readOnly={readOnly} reservation={reservation} />
                <ReservedResourcesCount readOnly={readOnly} reservation={reservation} />
                <ReservationCode readOnly={readOnly} reservation={reservation} />
              </Stack>
              <State readOnly={readOnly} reservation={reservation} />
            </Stack>
            <DatesRange readOnly={readOnly} reservation={reservation} />
            <Stack direction="row" gap={1} alignItems="center" mt={0.5}>
              <CreationDate readOnly={readOnly} reservation={reservation} />
              <Origin readOnly={readOnly} reservation={reservation} />
              <CustomerLanguage readOnly={readOnly} reservation={reservation} />
            </Stack>
          </Stack>
        )}
      </>
    )
  },
  (prev, next) => JSON.stringify(prev) === JSON.stringify(next),
)

export const Origin = React.memo(
  function Origin({ reservation, readOnly }: ReservationHeaderComponentsCommonProps) {
    const { t } = useTranslation()

    return (
      <Tooltip title={t('pms.booking.reservation.origin_title')}>
        <span>
          <Stack direction="row" alignItems="center">
            {reservation.origin.name === 'IBE' ? (
              <span className="ts-reservation-origin-ibe">
                <img src={logots} alt={'From IBE'} />
              </span>
            ) : reservation.origin.name === 'OTA' ? (
              <Portal readOnly={readOnly} reservation={reservation} />
            ) : null}
            <Typography variant="caption" ml={0.5} mr={1}>
              {reservation.origin.name}
            </Typography>
            <Typography variant="caption" noWrap>
              {reservation.voucher ? reservation.voucher : ''}
            </Typography>
          </Stack>
        </span>
      </Tooltip>
      /* <Stack>
        <Typography variant="caption" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {t('pms.booking.reservation.origin_title')}
        </Typography>
        <Stack direction="row" spacing={1} alignItems={'center'}>
          {reservation.origin.name === 'IBE' && (
            <span className="ts-reservation-origin-ibe">
              <img src={logots} alt={'From IBE'} />
            </span>
          )}
          <Typography variant="caption" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            <b>{reservation.origin.name}</b>
          </Typography>
          {reservation.origin.name === 'OTA' && <Portal readOnly={readOnly} reservation={reservation} />}
        </Stack>
      </Stack> */
    )
  },
  (prev, next) => prev.reservation.origin.name === next.reservation.origin.name,
)

export const Voucher = React.memo(
  function Voucher({ reservation }: ReservationHeaderComponentsCommonProps) {
    if (reservation.voucher) {
      return (
        <Typography variant="caption" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          <Trans i18nKey="pms.booking.reservation.voucher" values={{ voucher: reservation.voucher }} />
        </Typography>
      )
    } else {
      return <></>
    }
  },
  (prev, next) => prev.reservation.voucher === next.reservation.voucher,
)

export const CreationDate = React.memo(
  function CreationDate({ reservation }: ReservationHeaderComponentsCommonProps) {
    const formatDate = useFormatDate()
    const { t } = useTranslation()

    if (reservation.auditable.createdOn) {
      return (
        <Tooltip title={t('pms.booking.reservation.creation_date_label')}>
          <span>
            <Stack direction="row" alignItems="center">
              <HiCIcon>
                <FontAwesomeIcon icon={faClock} />
              </HiCIcon>
              <Typography variant="caption" ml={0.5} mr={1}>
                {formatDate(reservation.auditable.createdOn, 'PPP')}
              </Typography>
            </Stack>
          </span>
        </Tooltip>
      )
    } else {
      return <></>
    }
  },
  (prev, next) => prev.reservation.auditable.createdOn?.valueOf() === next.reservation.auditable.createdOn?.valueOf(),
)

function getLanguage(reservation: ReservationF) {
  if (reservation.reference?.customerPerson?.language) {
    return reservation.reference.customerPerson.language
  } else if (reservation.reference?.customerCompany?.language) {
    return reservation.reference.customerCompany.language
  } else if (reservation.reference?.mainGuest?.language) {
    return reservation.reference.mainGuest.language
  } else if (reservation.reference?.agency?.language) {
    return reservation.reference.agency.language
  }

  return undefined
}

export const CustomerLanguage = React.memo(
  function CustomerLanguage({ reservation }: ReservationHeaderComponentsCommonProps) {
    const { t } = useTranslation()
    const language = getLanguage(reservation)

    if (language) {
      return (
        <Tooltip title={t('pms.booking.reservation.totals.customer_language')}>
          <span>
            <Stack direction="row" alignItems="center">
              <HiCIcon>
                <FontAwesomeIcon icon={faGlobe} />
              </HiCIcon>
              <Typography variant="caption" ml={0.5} mr={1}>
                {language.toLocaleUpperCase()}
              </Typography>
            </Stack>
          </span>
        </Tooltip>
      )
    } else {
      return <></>
    }
  },
  (prev, next) => prev.reservation.auditable.createdOn?.valueOf() === next.reservation.auditable.createdOn?.valueOf(),
)

export const ReservationPolicy = React.memo(
  function ReservationPolicy({ reservation }: ReservationHeaderComponentsCommonProps) {
    const { t } = useTranslation()
    const formatDate = useFormatDate()
    const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null)
    const openPopover = Boolean(anchorEl)
    const { policy, minStart } = reservation
    const freeCancellationDateTime = useMemo(() => {
      if (policy?.cancellationPolicy.type === 'BYDATE' && minStart) {
        let calculatedDateTime = minStart
        if (policy?.cancellationPolicy.daysWithoutPenalties) calculatedDateTime = subDays(calculatedDateTime, policy.cancellationPolicy.daysWithoutPenalties)
        if (policy?.cancellationPolicy.hourWithoutPenalties) {
          const h = getHours(policy.cancellationPolicy.hourWithoutPenalties)
          const m = getMinutes(policy.cancellationPolicy.hourWithoutPenalties)
          calculatedDateTime = addHours(calculatedDateTime, h)
          calculatedDateTime = addMinutes(calculatedDateTime, m)
        }
        return calculatedDateTime
      }
    }, [policy, minStart])
    return (
      <>
        <Stack gap={1} direction="row" flexGrow={1}>
          <Tooltip title={t('pms.booking.reservation.booking_policy')}>
            <span>
              <Stack gap={0.5} direction="row">
                <HiCIcon>
                  <FontAwesomeIcon icon={faShieldHalved} />
                </HiCIcon>
                <Typography variant="caption">{reservation.policy?.name ?? ''}</Typography>
              </Stack>
            </span>
          </Tooltip>
          <HiCIcon
            sx={{ cursor: 'pointer' }}
            onClick={(event) => {
              setAnchorEl(event.currentTarget)
            }}
          >
            <FontAwesomeIcon icon={faInfoCircle} />
          </HiCIcon>
        </Stack>
        <Popover
          open={openPopover}
          anchorEl={anchorEl}
          onClose={() => {
            setAnchorEl(null)
          }}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
        >
          <Stack sx={{ padding: 2 }}>
            <b>{t('pms.settings.booking_policy.payment_rule_subtitle')}</b>
            {policy?.paymentRule.type && displayPaymentType(t, policy.paymentRule.type)}
            {policy?.paymentRule.guaranteedCreditCard ? <div>{t('pms.settings.booking_policy.cc_guarantee_required')}</div> : ''}
            <Divider light sx={{ my: 1 }} />
            <b>{t('pms.settings.booking_policy.cancellation_policy_type')}</b>
            {policy?.cancellationPolicy.type && `${displayCancellationPolicyType(t, policy.cancellationPolicy.type)}: `}
            <br />
            {freeCancellationDateTime ? `${formatDate(freeCancellationDateTime, 'PPPP, p')}` : ''}
            <br />
            {policy?.cancellationPolicy.penaltyType && (
              <>
                <br />
                <b>{t('pms.settings.booking_policy.cancellation_policy_penalty_type')}</b>
                <span>{displayCancellationPenaltyType(t, policy.cancellationPolicy.penaltyType)}</span>
                {policy?.cancellationPolicy.percentage ? `${policy?.cancellationPolicy.percentage * 100} %` : ''}
              </>
            )}
            <Divider light sx={{ my: 1 }} />
            <b>{t('pms.settings.booking_policy.no_show_rule_type_subtitle')}</b>
            {policy?.noShowRule.type && displayNoShowType(t, policy.noShowRule.type)}
            {policy?.noShowRule.percentage}
          </Stack>
        </Popover>
      </>
    )
  },
  (prev, next) => JSON.stringify(prev.reservation.policy) === JSON.stringify(next.reservation.policy),
)

const Reference = React.memo(function Reference({ icon, name, tooltip }: { icon: IconDefinition; name: string | undefined; tooltip: string }) {
  if (name) {
    return (
      <Tooltip title={tooltip}>
        <span>
          <Stack direction="row" alignItems="center" gap={0.5}>
            <HiCIcon>
              <FontAwesomeIcon icon={icon} />
            </HiCIcon>
            <Typography variant="caption" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {name}
            </Typography>
          </Stack>
        </span>
      </Tooltip>
    )
  } else {
    return <></>
  }
})

export function CustomerPerson({ name }: { name: string }) {
  const { t } = useTranslation()
  return <Reference icon={faUser} name={name} tooltip={t('pms.booking.reservation.reference.customer_person')} />
}

export function CustomerCompany({ name }: { name: string }) {
  const { t } = useTranslation()
  return <Reference icon={faBuilding} name={name} tooltip={t('pms.booking.reservation.reference.customer_company')} />
}

export function MainGuest({ name }: { name: string }) {
  const { t } = useTranslation()
  return <Reference icon={faBed} name={name} tooltip={t('pms.booking.reservation.reference.main_guest')} />
}

export function Agency({ name }: { name: string }) {
  const { t } = useTranslation()
  return <Reference icon={faHandshakeAngle} name={name} tooltip={t('pms.booking.reservation.reference.agency')} />
}

export function FaIcon(props: Omit<FontAwesomeIconProps, 'icon'> & { iconName: string }) {
  library.add(fas, far)
  const { iconName, ...iconProps } = props
  return <FontAwesomeIcon icon={iconName as IconName} {...iconProps} />
}

export const ReservationStateIcon = React.memo(
  function ReservationStateIcon(props: { reservationState: ReservationStateFragment }) {
    return <FaIcon iconName={props.reservationState.icon} color={props.reservationState.color} />
  },
  (prev, next) => prev.reservationState.icon === next.reservationState.icon && prev.reservationState.color === next.reservationState.color,
)

export const ReservationState = React.memo(
  function ReservationState({ reservationState }: { reservationState: ReservationStateFragment }) {
    return (
      <Stack direction="row" alignItems="center" spacing={2}>
        <ReservationStateIcon reservationState={reservationState} />

        <Typography //
          sx={{ height: '100%', width: '100%', textAlign: 'left' }}
          style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
        >
          {reservationState.name}
        </Typography>
      </Stack>
    )
  },
  (prev, next) => JSON.stringify(prev.reservationState) === JSON.stringify(next.reservationState),
)

function getPhoneAndEmail(customer: ReferenceCustomerFragment) {
  const contactInfo = { email: '', phone: '' }

  if (customer) {
    contactInfo.email = customer.mailContacts?.find((mail) => mail.type === 'MAIN')?.value ?? ''
    contactInfo.phone = customer.phoneContacts?.find((phone) => phone.type === 'MAIN')?.value ?? ''
  }

  return contactInfo
}

export function getContactInfo(reservation: ReservationF): { email: string; phone: string } {
  let contactInfo = { email: '', phone: '' }

  if (reservation.reference.customerPerson) {
    contactInfo = getPhoneAndEmail(reservation.reference.customerPerson)
  } else if (reservation.reference.customerCompany) {
    contactInfo = getPhoneAndEmail(reservation.reference.customerCompany)
  } else if (reservation.reference.mainGuest) {
    contactInfo = getPhoneAndEmail(reservation.reference.mainGuest)
  } else if (reservation.reference.agency) {
    contactInfo = getPhoneAndEmail(reservation.reference.agency)
  }

  return contactInfo
}

const useReservationPropertyInfo = (reservation: ReservationF) => {
  const property = useProperty()

  const subAddressOfReservation = useMemo(() => {
    if (property?.subAddresses && property.subAddresses.length > 0) {
      let assignedResourceTypeId: string | undefined = undefined
      const firstRR = reservation.reservedResources?.[0]
      if (firstRR) {
        const firstOccupancy = firstRR.resourceOccupancies?.[0]
        if (firstOccupancy) {
          assignedResourceTypeId = firstOccupancy.assignedResourceType.id
        }
      }
      if (assignedResourceTypeId) {
        const result = property.subAddresses.find((fc) => (fc.resourceTypeIds && assignedResourceTypeId ? fc.resourceTypeIds.includes(assignedResourceTypeId) : false))
        if (result) return result
      }
    }
    return undefined
  }, [reservation, property])

  return subAddressOfReservation
}

export const ContactInfo = React.memo(
  function ContactInfo({ contactInfo, readOnly, onMailClick }: { readOnly: boolean; contactInfo: { email: string; phone: string }; onMailClick: () => void }) {
    return (
      <Stack gap={2} direction="row" alignItems="center">
        {contactInfo.phone.length > 0 && (
          <Stack direction="row" alignItems="center" gap={1}>
            <HiCIcon>
              <FontAwesomeIcon icon={faPhone} />
            </HiCIcon>
            <Button
              variant="text"
              size="small"
              sx={{ padding: '0px!important', fontSize: '12px!important', lineHeight: '16px!important', fontWeight: '400', height: 'unset!important', minHeight: '0px!important' }}
              href={`tel:${contactInfo.phone}`}
              disabled={readOnly}
            >
              {contactInfo.phone}
            </Button>
          </Stack>
        )}
        {contactInfo.email.length > 0 && (
          <Stack direction="row" alignItems="center" gap={1}>
            <HiCIcon>
              <FontAwesomeIcon icon={faAt} />
            </HiCIcon>
            <Button
              variant="text"
              size="small"
              sx={{ padding: '0px!important', fontSize: '12px!important', lineHeight: '16px!important', fontWeight: '400', height: 'unset!important', minHeight: '0px!important' }}
              disabled={readOnly}
              onClick={onMailClick}
            >
              {contactInfo.email}
            </Button>
          </Stack>
        )}
      </Stack>
    )
  },
  (prev, next) => JSON.stringify(prev.contactInfo) === JSON.stringify(next.contactInfo),
)

export function SelfCheckin({ reservation, readOnly, reservationLink }: ReservationHeaderComponentsCommonProps & { reservationLink: string | null }) {
  const { t } = useTranslation()
  const showSuccess = useSuccessSnackBar()
  const { privacySettings } = useTenant()
  const property = useProperty()
  const formatDate = useFormatDate()
  // const [sendReservationEmail] = useSendReservationEmailMutation()
  const hasDoneSelfCheckin = useMemo(() => {
    return reservation.reservedResources?.some((rr) => rr.guestOccupancies?.some((go) => go.wciState === 'CREATED')) ?? false
  }, [reservation])

  const manualCheckinExecuted = useMemo(() => {
    return reservation.reservedResources?.some((rr) => rr.phase === ReservedResourcePhase.CHECKIN && rr.guestOccupancies?.some((go) => !go.wciState))
  }, [reservation])

  const contactInfo = getContactInfo(reservation)
  const language = getLanguage(reservation)
  const subAdressForReservation = useReservationPropertyInfo(reservation)

  if (!reservation.webCheckinEnabled) {
    return null
  }

  const onCopyLink = useCallback(() => {
    if (!reservationLink) return
    navigator.clipboard.writeText(reservationLink).then(() => {
      showSuccess(t('pms.booking.reservation.action.reservation_link_copied'))
    })
  }, [reservationLink])

  const onSendByWhatsapp = useCallback(() => {
    if (contactInfo.phone && reservationLink && reservation.minStart !== undefined && property) {
      const message = t('pms.booking.reservation.send_whatsapp_wci_link.message', {
        property_name: subAdressForReservation ? subAdressForReservation.name : property?.name,
        start_date: formatDate(reservation.minStart),
        link: reservationLink,
        lng: language ?? 'it',
      })
      sendWhatsappMessage(contactInfo.phone, message)
    }
  }, [contactInfo.phone, reservationLink, language, subAdressForReservation])

  /* const onSendWCIEmailButtonClick = useCallback(async () => {
    // Il bottone deve essere abilitato solo se non è già stato fatto il WCI e se l'utente ha un indirizzo email.
    if (contactInfo.email) {
      setLoading(true)
      try {
        const result = await sendReservationEmail({
          variables: {
            input: {
              mailTemplateCode: 'WCI',
              tenantId: reservation.tenantId,
              propertyId: propertyId,
              reservationId: reservation.id,
              sendTo: contactInfo.email,
            },
          },
        })
        const response = result.data?.sendReservationEmail?.emailState
        if (response === 'SEND') showSuccess(t('commons.email_sent'))
      } catch (error) {
        showError(error)
      } finally {
        setLoading(false)
      }
    }
  }, [sendReservationEmail, contactInfo.email]) */

  return (
    <Stack>
      <Box>
        {(!manualCheckinExecuted || hasDoneSelfCheckin) && (
          <HicChip
            text={hasDoneSelfCheckin ? t('pms.booking.reservation.guest_portal.done') : t('pms.booking.reservation.guest_portal.not_done')}
            icon={hasDoneSelfCheckin ? undefined : faHourglassStart}
            image={hasDoneSelfCheckin ? <FontAwesomeIcon icon={faCheck} color="var(--White)" /> : undefined}
            outlined={hasDoneSelfCheckin ? false : true}
            variant={hasDoneSelfCheckin ? 'light' : undefined}
            color={hasDoneSelfCheckin ? 'var(--success)' : undefined}
          />
        )}
      </Box>
      <Stack direction="row" alignItems="center" columnGap={1} flexWrap="wrap">
        {(!manualCheckinExecuted || hasDoneSelfCheckin) && (
          <Button size="small" variant="text" onClick={onCopyLink} disabled={readOnly || !privacySettings} startIcon={<FontAwesomeIcon icon={faCopy} />}>
            {t('pms.booking.reservation.action.copy_reservation_link')}
          </Button>
        )}
        <Button size="small" variant="text" onClick={onSendByWhatsapp} disabled={readOnly || !contactInfo.phone || !privacySettings} startIcon={<FontAwesomeIcon icon={faWhatsapp} />}>
          {t('pms.booking.reservation.action.send_whatsapp_wci_link')}
        </Button>
        {/* <Button
              id="rrd-send-checkin-email-button"
              size="small"
              variant="outlined"
              onClick={onSendWCIEmailButtonClick}
              disabled={readOnly || !contactInfo.email || !privacySettings}
              startIcon={<FontAwesomeIcon icon={faEnvelope} />}
            >
              {lg ? t('pms.booking.reservation.action.send_email_wci_link') : ''}
            </Button> 
        */}
      </Stack>
      <PrivacyNotConfiguredWarning messageKey={'pms.booking.reservation.missing_privacy_configuration'} />
    </Stack>
  )
}

export function ReferenceTags({ tags }: { tags: TagFragment[] }) {
  return (
    <Stack direction="row" gap={1} flexWrap="wrap" alignItems="center">
      {tags.map((tag) => (
        <HicChipForTag key={tag.id} color={tag.color} label={tag.label} />
      ))}
    </Stack>
  )
}

const convertHexToRGBA = (hexCode: string, opacity = 1) => {
  let hex = hexCode.replace('#', '')

  if (hex.length === 3) {
    hex = `${hex[0]}${hex[0]}${hex[1]}${hex[1]}${hex[2]}${hex[2]}`
  }

  const r = parseInt(hex.substring(0, 2), 16)
  const g = parseInt(hex.substring(2, 4), 16)
  const b = parseInt(hex.substring(4, 6), 16)

  /* Backward compatibility for whole number based opacity values. */
  if (opacity > 1 && opacity <= 100) {
    opacity = opacity / 100
  }

  return `rgba(${r},${g},${b},${opacity})`
}
